import { useQueryClient } from '@tanstack/react-query';
import type {
  ListBuyingOrdersResponse,
  ListSupplierSummariesWithCartsResponse,
} from '@erp/rpc';
import { useGT } from 'gt-react';
import { toast } from 'src/components/ui/toast';
import { orpc } from 'src/api/rpc/client';
import {
  useFulfillmentCandidates,
  useOverrideOption,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { useLineBulkEditTargets } from './lineBulkEditContext';
import { VendorCandidatesSection } from './VendorCandidatesSection';
import { applyBulkFulfillmentOverrides } from '../utils/bulkOverrideApply';
import type { BulkOverrideForm } from '../utils/bulkFulfillmentOverride';
import { effectiveQuantity } from '../utils/lineQuantity';
import { planOverrideInputForLine } from '../utils/planOverrideInputForLine';
import {
  canSwitchVendor,
  findLoadedCart,
  isWholeCartQuery,
  loadedCarts,
  queryInputOf,
  type VendorCandidate,
} from '../utils/vendorCandidateRows';

function vendorSwitchForm(vendorId: string): BulkOverrideForm {
  return {
    action: 'buy',
    quantity: 0,
    applyQuantityToAllLines: false,
    vendorId,
    sourceId: '',
  };
}

/**
 * The unit cost dropdown's supplier picker for one line. It mounts with the
 * open dropdown, so candidates load per opened line, not per board row.
 */
export function VendorSwitchSection({
  line,
  currentSupplierId,
  lineById,
}: {
  line: AssignmentLine;
  currentSupplierId: string | null | undefined;
  lineById: (suggestionId: string) => AssignmentLine | undefined;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const editTargets = useLineBulkEditTargets();
  const override = useOverrideOption();
  const lineQuantity = effectiveQuantity(line);
  const candidates = useFulfillmentCandidates({
    productV3Key: line.productV3Key ?? '',
    destinationWarehouseId: line.destinationWarehouseId,
    ...(lineQuantity > 0 ? { buyQuantity: lineQuantity } : {}),
    includeUnpriced: true,
  });

  // The board's loaded carts, read when the dropdown opens.
  const carts = loadedCarts(
    queryClient
      .getQueriesData<ListBuyingOrdersResponse>({
        queryKey:
          orpc.procurement.replenishmentSuggestions.listBuyingOrders.key(),
      })
      // Line filters narrow cartTotal to the matching lines: only whole
      // carts measure a MOV gap.
      .flatMap(([queryKey, data]) =>
        isWholeCartQuery(queryInputOf(queryKey)) ? [data] : [],
      ),
    queryClient
      .getQueriesData<ListSupplierSummariesWithCartsResponse>({
        queryKey:
          orpc.procurement.replenishmentSuggestions.listSupplierSummariesWithCarts.key(),
      })
      .map(([, data]) => data),
  );
  const cartFor = (vendor: VendorCandidate) =>
    findLoadedCart(carts, vendor.supplierId, vendor.shipToWarehouseId);

  const switchFailed = () =>
    toast.error(
      gt('Could not switch the supplier. Try again.', {
        context: 'replenishment supplier picker error',
      }),
    );

  const switchBulk = (targetIds: readonly string[], form: BulkOverrideForm) => {
    const bulkLines = targetIds
      .map((id) => (id === line.suggestionId ? line : lineById(id)))
      .filter((target) => target != null);
    // Every selected line must be switchable on its own: a split plan's
    // stock transfer would be dropped by a plain vendor buy.
    const switchable = bulkLines.filter(canSwitchVendor);
    const kept = bulkLines.length - switchable.length;
    if (kept > 0) {
      toast.error(
        gt(
          'Kept {n} lines as they are: split plans and released lines cannot switch supplier.',
          {
            n: kept,
            context:
              'replenishment supplier picker: some selected lines were not switched',
          },
        ),
      );
    }
    if (switchable.length === 0) return;
    applyBulkFulfillmentOverrides(
      queryClient,
      gt,
      switchable,
      switchable.length,
      form,
      (input) => override.mutateAsync(input),
    ).catch(switchFailed);
  };

  const switchVendor = (vendor: VendorCandidate) => {
    const form = vendorSwitchForm(vendor.supplierId);
    const targetIds = editTargets(line.suggestionId);
    if (targetIds.length > 1) {
      switchBulk(targetIds, form);
      return;
    }
    const planned = planOverrideInputForLine(line, form, {
      vendors: [vendor],
      transferSources: [],
    });
    if (planned.status !== 'applied') return;
    // Feedback hangs off the promise, not mutate's callbacks: choosing an
    // item closes the dropdown and unmounts this section mid-request.
    override.mutateAsync(planned.input).then(
      () =>
        toast.success(
          gt('Moved to {vendor}', {
            vendor: vendor.supplierName,
            context:
              'replenishment supplier picker: the line moved to the chosen vendor cart',
          }),
        ),
      switchFailed,
    );
  };

  return (
    <div className="border-t p-1 pt-2">
      <VendorCandidatesSection
        vendors={candidates.data?.vendors ?? []}
        isLoading={candidates.isLoading}
        isError={candidates.isError}
        onRetry={() => void candidates.refetch()}
        currentSupplierId={currentSupplierId}
        quantity={lineQuantity}
        disabled={override.isPending}
        cartFor={cartFor}
        onSelect={switchVendor}
      />
    </div>
  );
}
